import React, { useState, useEffect } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody
} from 'reactstrap'
import { IPromotion } from './index'
import { toast } from 'react-toastify'
import DeleteModal from 'Components/Common/DeleteModal'
import { useDebouncedCallback } from 'use-debounce'
import { Spinner } from 'reactstrap'
import { ISort } from 'lib/type'
import { showDiscountType, formatTime } from 'lib/util'
import { getPromotionStatus, getUsageInfo } from 'lib/promotionUtils'

interface IProds {
  fetching: boolean
  promotionRecords: IPromotion[]
  handleOpenDetails: (arg: string) => void
  setOpenPromotionPopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeletePromotion: (arg: string) => Promise<string>
  searchPromotion: (arg: string) => void
  sortAction: ISort
  setSortAction: (arg: ISort) => void
  setCanEdit: (arg: boolean) => void
}

const PromotionsTable: React.FC<IProds> = ({
  promotionRecords,
  handleOpenDetails,
  setOpenPromotionPopup,
  setEditMode,
  fetching,
  handleDeletePromotion,
  searchPromotion,
  sortAction,
  setSortAction,
  setCanEdit
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [promotionSelectedId, setPromotionSelectedId] = useState<string>()
  const [query, setQuery] = useState('')
  // 🔒 2026-08-17: varsayilan FAIL-CLOSED olmali. Eskiden `true` ile
  // basliyordu; asagidaki useEffect yalnizca yetki listesinde BU sayfa
  // bulunursa deger yaziyor. Yani operatorun bu sayfada hic yetkisi yoksa
  // (ya da oturum verisi bozuksa) baslangic degeri KALIYOR ve tum
  // Ekle/Duzenle/Sil dugmeleri ACIK gorunuyordu.
  // Adminler asagidaki `userType === '1'` daliyla ayrica ele aliniyor.
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false
  })

  const handleAddPromotion = () => {
    setEditMode(false)
    setOpenPromotionPopup(true)
  }

  const handleEditPromotion = (id: string) => {
    setEditMode(true)
    setOpenPromotionPopup(true)
    handleOpenDetails(id)
  }

  const handleDeletePromotionRecord = async (id: string) => {
    const res = handleDeletePromotion(id)

    const message = await res
    if (message === 'SUCCESSFULL') {
      toast.success('Deleted a Promotion successfully')
    }
  }

  const handleShowModalDelete = (id: string) => {
    setShowModalDelete(true)
    setPromotionSelectedId(id)
  }

  const handleDelete = (id: string) => {
    handleDeletePromotionRecord(id)
    setShowModalDelete(false)
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    searchPromotion(searchTerm)
  }, 1000)

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  const handleActionSort = (sortBy: string) => {
    if (sortAction.sortBy === sortBy) {
      setSortAction({
        ...sortAction,
        sortDirection: sortAction.sortDirection === 'DESC' ? 'ASC' : 'DESC'
      })
    } else {
      setSortAction({
        sortDirection: 'DESC',
        sortBy: sortBy
      })
    }
  }

  const showBooths = (data: any, rowKey: string) => {
    const names: string[] = !data
      ? []
      : Object.values(data).map((boothEvent: any) => boothEvent.booth?.name).filter(Boolean)
    if (names.length === 0) return <span className='text-muted'>-</span>

    // Az sayıda kabinde (≤3) rozetleri doğrudan göster. Çok kabinde (ör. 55) tek
    // bir "N kabin" rozeti göster; tıklayınca tüm liste popover içinde açılır.
    // Böylece Booth sütunu kabin sayısı arttıkça tabloyu yatayda genişletmez.
    if (names.length <= 3) {
      return (
        <div style={{ maxWidth: '220px' }} className='d-flex flex-wrap gap-1'>
          {names.map((name: string, i: number) => (
            <span key={i} className='badge bg-soft-primary text-primary'>{name}</span>
          ))}
        </div>
      )
    }

    const targetId = `booth-pop-${rowKey}`
    return (
      <>
        <span
          id={targetId}
          className='badge bg-soft-primary text-primary'
          style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          {names.length} kabin <i className='mdi mdi-chevron-down align-middle'></i>
        </span>
        <UncontrolledPopover target={targetId} trigger='legacy' placement='bottom-start'>
          <PopoverHeader>{names.length} kabin</PopoverHeader>
          <PopoverBody style={{ maxHeight: '240px', overflowY: 'auto', minWidth: '180px' }}>
            <div className='d-flex flex-wrap gap-1'>
              {names.map((name: string, i: number) => (
                <span key={i} className='badge bg-soft-primary text-primary'>{name}</span>
              ))}
            </div>
          </PopoverBody>
        </UncontrolledPopover>
      </>
    )
  }

  useEffect(() => {
    const userType = sessionStorage.getItem('userType')

    // Type 1 = Admin - full access to everything
    if (userType === '1') {
      setPermissions({
        canCreate: true,
        canEdit: true,
        canDelete: true
      })
      setCanEdit(true)
      return
    }

    // Type 2 = Regular user - use role-based permissions
    const userRoles = sessionStorage.getItem('userRoles')
      ? JSON.parse(atob(sessionStorage.getItem('userRoles')!))
      : null
    if (userRoles) {
      userRoles.forEach((role: any) => {
        if (role.page === 'Promotions') {
          setPermissions({
            canCreate: role.canCreate,
            canEdit: role.canEdit,
            canDelete: role.canDelete
          })
          setCanEdit(role.canEdit || role.canCreate)
        }
      })
    }
  }, [setCanEdit])

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex'>
              <div
                className='search-box d-inline-block d-flex align-items-center'
                style={{ flex: 1 }}
              >
                <input
                  id='search-bar-action-price'
                  className='form-control search'
                  placeholder='Search Promotions'
                  onChange={(e) => handleInputChange(e)}
                  value={query}
                  style={{ maxWidth: '400px' }}
                />
                <i className='bx bx-search-alt search-icon'></i>
                {permissions.canCreate && (
                  <Button
                    className='p-2 ms-3'
                    color='primary'
                    onClick={(e) => handleAddPromotion()}
                  >
                    <div className='flex align-items-center'>
                      {fetching ? (
                        <Spinner size='sm'> Loading... </Spinner>
                      ) : (
                        <>
                          <i className='mdi mdi-plus'></i>
                          <span className='fw-semibold ms-1'>New Promotion</span>
                        </>
                      )}
                    </div>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card'>
                <table className='table align-middle table-nowrap table-striped-columns mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col' onClick={() => handleActionSort('name')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Name</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'name' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'name' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('code')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Code</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'code' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'code' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('startDateTime')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Start Date Time</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'startDateTime' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'startDateTime' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('finishDateTime')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Finish Date Time</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'finishDateTime' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'finishDateTime' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('discountType')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Discount Type</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'discountType' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'discountType' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('discountValue')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Discount Value</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'discountValue' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'discountValue' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col'>Booth</th>
                      <th scope='col'>Usage</th>
                      <th scope='col'>Status</th>
                      <th scope='col' style={{ width: '150px' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotionRecords.map((promotion, index) => {
                      return (
                        <tr key={index}>
                          <td>{promotion.name}</td>
                          <td>{promotion.code}</td>
                          <td>
                            {promotion.startDateTime ? formatTime(promotion.startDateTime) : ''}
                          </td>
                          <td>
                            {promotion.finishDateTime ? formatTime(promotion.finishDateTime) : ''}
                          </td>
                          <td>{showDiscountType(promotion.discountType)}</td>
                          <td>{promotion.discountValue}</td>
                          <td>{showBooths(promotion.boothPromotions, (promotion?._id as string) || String(index))}</td>
                          <td>
                            <span className='text-muted small'>{getUsageInfo(promotion)}</span>
                          </td>
                          <td>
                            <span className={`badge bg-${getPromotionStatus(promotion).color}`}>
                              {getPromotionStatus(promotion).label}
                            </span>
                          </td>
                          <td>
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              onClick={(e) => handleEditPromotion(promotion?._id as string)}
                            >
                              Details
                            </button>
                            {permissions.canDelete && (
                              <button
                                type='button'
                                className='btn btn-sm btn-danger ms-2'
                                onClick={(e) => handleShowModalDelete(promotion?._id as string)}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={() => handleDelete(promotionSelectedId as string)}
        onCloseClick={() => setShowModalDelete(false)}
      />
    </React.Fragment>
  )
}
export default PromotionsTable
